import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { timer } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SvgComponent } from '../svg/svg.component';
import { TransactionData } from '../api-request';
import { ApiResponse, TransactionFees } from '../api-response';
import { AppService } from '../app.service';
import { StringHelper, ICopyToClipboard } from '../strings-helper';
import { postTransactionMessage } from '../events';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'ussd-channel',
    templateUrl: './ussd-channel.component.html',
    styleUrls: ['./ussd-channel.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, SvgComponent]
})
export class UssdChannelComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  @Output() onComplete: any = new EventEmitter<any>();
  @Output() onClose: any = new EventEmitter<any>();
  @Input() activeTab: any = null;
  error: boolean = false;

  state = {
    visible: false,
    isInitialized: false,
    completed: false,
    loaded: false,
    bankSelected: false,
    verifyingUssd: false,
    error: false,
  };
  responseData: any;
  bankOption: any = null;
  transactionData!: TransactionData;
  amount: any;
  transactionFees!: TransactionFees;
  reference!: string;
  errorMessage!: string;
  copyMessage: string = 'Copied!';
  copied: boolean = false;

  time_in_minutes = 10;

  display: string = `${10}`;
  public timerInterval: any;
  expiresAt!: number;
  ussdExpired: boolean = false;
  loadingMessage: string = 'Please wait';
  queryTime: number = environment.txnQueryTime / 2; // 10 minutes

  private setState(key: any, value: any) {
    this.state = { ...this.state, [key]: value };
    this.cd.detectChanges();
  }

  private timer$: any;
  callback: any = {};

  constructor(
    private cd: ChangeDetectorRef,
    private appService: AppService,
    private el: ElementRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeTab'].currentValue == 'ussd') {
      this.appService.getData().subscribe({
        next: (res) => {
          if (res) {
            this.transactionData = res.txnData;
            this.reference = res.txnRef;
            this.transactionFees = res.transactionFees;
            this.amount = StringHelper.toMajorAmount(
              +this.transactionData.amount + this.transactionFees.WEB_USSD,
            );
          }
        },
        error: (err) => {
          console.log(err);
        },
      });

      const existing = this.appService.getUssdState(this.reference);
      if (existing) {
        this.responseData = existing.bankUssdCodes;
        this.setState('visible', true);
        this.listenForTransactionStatus();
        if (
          existing.selectedBank &&
          existing.expiresAt &&
          Date.now() < existing.expiresAt
        ) {
          this.bankOption = existing.selectedBank;
          this.setState('bankSelected', true);
          this.expiresAt = existing.expiresAt;
          this.startCountDown();
        }
        return;
      }

      const data = {
        ussdRequestType: 'USSD_CODE_GENERATION',
        reference: this.reference,
      };
      this.cancelProcess();
      if (!this.state.visible) {
        this.generateCode(data);
      }
    }
  }

  ngAfterViewInit(): void {
    console.log('after view init');

    this.appService.getData().subscribe({
      next: (res) => {
        if (res) {
          this.transactionData = res.txnData;
          this.reference = res.txnRef;
          this.transactionFees = res.transactionFees;
          this.amount = StringHelper.toMajorAmount(
            +this.transactionData.amount + this.transactionFees.WEB_USSD,
          );
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  ngOnInit(): void {}

  formatTime(value: number) {
    `${Math.floor(value / 60)}:${('0' + (value % 60)).slice(-2)}`;
  }

  startCountDown() {
    this.ussdExpired = false;
    this.timer();
  }

  stopCountDown() {
    clearInterval(this.timerInterval);
  }

  timer() {
    clearInterval(this.timerInterval);

    const tick = () => {
      const seconds = Math.max(
        0,
        Math.round((this.expiresAt - Date.now()) / 1000),
      );
      const prefix = Math.floor(seconds / 60) < 10 ? '0' : '';
      const textSec = ('0' + (seconds % 60)).slice(-2);
      this.display = `${prefix}${Math.floor(seconds / 60)}:${textSec}`;

      if (seconds === 0) {
        this.ussdExpired = true;
        if (this.reference && this.responseData) {
          this.appService.setUssdState({
            reference: this.reference,
            bankUssdCodes: this.responseData,
            selectedBank: null,
            expiresAt: null,
          });
        }
        clearInterval(this.timerInterval);
      }
    };

    tick();
    this.timerInterval = setInterval(tick, 1000);
  }

  generateCode(data: any) {
    this.setState('error', false);
    this.setState('verifyingUssd', true);
    this.appService.generateUssdCode(data).subscribe({
      next: (res) => {
        if (res.success) {
          this.responseData = res.data.bankUssdCodes;
          this.setState('verifyingUssd', false);
          this.setState('visible', true);
          this.appService.setUssdState({
            reference: this.reference,
            bankUssdCodes: this.responseData,
            selectedBank: null,
            expiresAt: null,
          });
          this.listenForTransactionStatus();
        } else {
          this.setState('verifyingUssd', false);
          this.setState('error', true);
          this.errorMessage =
            'An error occurred while generating USSD code please try again';
        }
      },
      error: (err) => {
        console.log(err);
        this.errorMessage = StringHelper.getErrorMessage(err.error);
        this.setState('verifyingUssd', false);
        this.setState('error', true);
        this.stopCountDown();
      },
    });
  }

  listenForTransactionStatus() {
    this.beginQuery();
  }

  confirmTransaction() {
    this.setState('verifyingUssd', true);
    const data = {
      ussdRequestType: 'USSD_STATUS_QUERY',
      reference: this.reference,
    };
    this.appService.queryUssdTransaction(data).subscribe({
      next: (res: ApiResponse) => {
        if (res.success) {
          this.setState('verifyingUssd', false);
          this.callback.url = res.data.redirectHtml;
          this.paymentDone();
        } else {
          const errorMessage = StringHelper.getErrorMessage(res);
          if (errorMessage == 'PENDING_CONFIRMATION') {
            this.beginQuery();
            return;
          }
          this.setState('verifyingUssd', false);
          this.paymentFailed();
        }
      },
      error: (err) => {
        this.errorMessage = StringHelper.getErrorMessage(err.error);
        this.setState('verifyingUssd', false);
        this.setState('error', true);
        this.paymentFailed();
      },
    });
  }

  beginQuery() {
    this.startInterval();
  }

  startInterval() {
    this.timer$ = timer(0, 10000).subscribe((tick: any) => {
      this.loadingMessage = `Confirming Transaction Status.`;
      if (tick == this.queryTime) {
        this.loadingMessage = `It's taking longer than expected to confirm your payment.`;
        this.errorMessage = `We could not confirm your payment.`;
        setTimeout(() => {
          this.setState('verifyingUssd', false);
          this.setState('error', true);
          this.paymentFailed();
        }, 3000);
      } else if (tick == this.queryTime + 2) {
        this.stopInterval();
        // this.onClose.emit();
      } else {
        // Your API call, which will be performed every 10000 milliseconds
        this.queryTransaction();
      }
    });
  }

  stopInterval() {
    this.timer$.unsubscribe();
  }

  queryTransaction() {
    const data = {
      ussdRequestType: 'USSD_STATUS_QUERY',
      reference: this.reference,
    };
    this.appService.queryUssdTransaction(data).subscribe({
      next: (res: ApiResponse) => {
        if (res.success) {
          const isSuccessful = res.data.serviceResponseCodes == 'COMPLETED';
          if (isSuccessful) {
            this.setState('verifyingUssd', false);
            this.paymentDone();
          }
        }
      },
      error: (err) => {
        this.errorMessage = StringHelper.getErrorMessage(err.error);
        this.setState('verifyingUssd', false);
        this.setState('error', true);
        this.paymentFailed();
      },
    });
  }

  cancelProcess() {
    this.setState('verifyingUssd', false);
    this.setState('bankSelected', false);
    this.setState('error', false);
    this.stopCountDown();
    if (this.reference && this.responseData) {
      this.appService.setUssdState({
        reference: this.reference,
        bankUssdCodes: this.responseData,
        selectedBank: null,
        expiresAt: null,
      });
    }
  }

  selectBank() {
    this.setState('bankSelected', true);
    this.expiresAt = Date.now() + this.time_in_minutes * 60000;
    this.appService.setUssdState({
      reference: this.reference,
      bankUssdCodes: this.responseData,
      selectedBank: this.bankOption,
      expiresAt: this.expiresAt,
    });
    this.startCountDown();
  }

  paymentDone() {
    this.onComplete.emit(this.callback);
    const domEvent = new CustomEvent('paymentDone');
    this.el.nativeElement.dispatchEvent(domEvent);
  }

  paymentFailed() {
    const error = {
      reference: this.reference,
      message: this.errorMessage,
      status: 'failure',
    };
    postTransactionMessage('error', error);
    const domEvent = new CustomEvent('paymentFailed');
    this.el.nativeElement.dispatchEvent(domEvent);
  }

  copyToClipboard() {
    // Get the text field
    const copyText = (<HTMLElement>document.getElementById('acc-no')).innerHTML;
    const arg: ICopyToClipboard = {
      target: 'acc-no',
      value: copyText,
      message: this.copyMessage,
    };
    StringHelper.copyToClipboard(arg).then((res) => {
      this.copied = true;
      setTimeout(() => {
        this.copied = false;
      }, 2000);
    });
  }

  ngOnDestroy(): void {
    this.timer$?.unsubscribe();
    clearInterval(this.timerInterval);
  }
}
